<template>
  <div id="app" class="no-select">
    <!-- Grid Size Selector (shown initially) -->
    <div v-if="showGridSelector" class="grid-size-selector">
      <h2>Select Grid Size</h2>
      <div class="grid-options">
        <div 
          v-for="option in gridSizeOptions" 
          :key="option.size"
          class="grid-option"
          @click="onGridSizeSelected(option.size)"
        >
          <div class="size-label">{{ option.label }}</div>
        </div>
      </div>
    </div>

    <!-- Game Board (hidden initially) -->
    <div v-else class="game-container">
      <div class="navbar">
        <button @click="showGridSelector = true" class="back-btn">Back</button>
        <div class="game-info">
          <span class="grid-size-display">{{ gameState.gridSize }}×{{ gameState.gridSize }}</span>
          <span class="error-count">{{ gameState.mismatchCount }}</span>
        </div>
        <div class="game-controls">
          <button 
            v-if="isLastButtonActive" 
            @click="showLastPair" 
            :disabled="gameState.isProcessing"
            class="last-btn"
          >
            Last
          </button>
          <button @click="startNewGame(gameState.gridSize)" class="new-game-btn">
            New Game
          </button>
        </div>
      </div>

      <!-- Game Grid -->
      <div class="game-grid-container">
        <div 
          class="game-grid" 
          :style="gridContainerStyle"
        >
          <!-- Tiles will be positioned absolutely within this container -->
          <div 
            v-for="tile in gameState.tiles"
            :key="tile.id"
            class="memory-tile"
            :class="{ 'face-up': tile.faceUp, 'face-down': !tile.faceUp }"
            :style="getTileStyle(tile)"
            @click="onTileClick(tile.id)"
          >
            <div class="tile-content">
              <div class="tile-face" :style="getTileFaceStyle(tile)">
                {{ tile.content.value }}
              </div>
              <div class="tile-back"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Game Complete Overlay -->
      <div v-if="gameState.gameComplete" class="completion-overlay">
        <div class="completion-stats">
          <h2>Puzzle Complete</h2>
          
          <div class="completion-summary">
            <div class="grid-info">
              <span class="grid-size">{{ gameState.gridSize }}×{{ gameState.gridSize }}</span>
            </div>
            
            <div class="current-score">
              <span class="value">{{ errorsText }}</span>
              <span v-if="isNewBest" class="new-best">New Personal Best!</span>
            </div>
            
            <div class="stats-summary">
              <div class="stats-line">Best: {{ bestText }}, Average: {{ averageText }}</div>
              <div class="games-played">Games played: {{ gridStats.gamesPlayed }}</div>
            </div>
          </div>
          
          <div class="actions">
            <button @click="startNewGame(gameState.gridSize)" class="new-game-btn">
              New Game
            </button>
            <button @click="showGridSelector = true" class="select-size-btn">
              Back
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import type { GameState, Tile, SlotConfig, GameScore } from './types/game'
import { useScoreTracking } from './composables/useScoreTracking'

// ============================================================================
// CONSTANTS AND THEME
// ============================================================================

const MEMORYKU_THEME = {
  // Core colors
  SLOT_BACKGROUND: '#6b7280',      // Gray-500 - puzzle grid
  SLOT_BORDER: '#000000',          // Black - grid definition
  TILE_BACKGROUND: '#ffffff',      // White - number tiles
  TILE_TEXT: '#1f2937',            // Dark gray - numbers
  GAME_BACKGROUND: '#f3f4f6',      // Light gray - puzzle area
  SELECTED_BORDER: '#ffd700',      // Gold - selected tile
  
  // ColorBrewer Set3 palette for numbers (maintain exact order)
  COLOR_PALETTE: [
    '#8dd3c7',  // 0: Teal
    '#bc80bd',  // 1: Magenta  
    '#fb8072',  // 2: Soft red
    '#80b1d3',  // 3: Blue
    '#fdb462',  // 4: Orange
    '#b3de69',  // 5: Green
    '#fccde5',  // 6: Grayish pink
    '#ccebc5',  // 7: Grayish lime green
    '#ffcc33',  // 8: Yellow
    '#bebada',  // 9: Lavender
  ],
  
  // Animation timing (preserve exact oldcode values)
  TILE_FLIP_DURATION: 600,
  MATCH_PROCESSING_DELAY: 1000,
  MISMATCH_DISPLAY_DURATION: 700,
  CELEBRATION_DURATION: 3000
}

const LAYOUT_CONSTANTS = {
  NAVBAR_HEIGHT_MIN: 30,
  NAVBAR_HEIGHT_MAX: 50,
  NAVBAR_HEIGHT_SCALE: 0.08,
  STATUS_MESSAGE_SPACE: 100,
  LEFT_MARGIN_DESKTOP: 50,
  LEFT_MARGIN_MOBILE: 20,
  SLOT_MIN_SIZE: 30,
  SLOT_MAX_SIZE: 120,
  BORDER_WIDTH: 1,
  BORDER_RADIUS: 0
}

// ============================================================================
// REACTIVE STATE
// ============================================================================

// Game state (start simple with refs)
const gameState = ref<GameState>({
  gridSize: 4,
  tiles: [],
  slots: [],
  flippedTiles: [],
  isProcessing: false,
  mismatchCount: 0,
  gameComplete: false,
  gameInProgress: false,
  lastPairFlipped: [],
  lastMismatchTiles: [],
  showingLastMismatch: false
})

// Click queue for handling clicks during animations
const clickQueue = ref<string[]>([])
const processingQueue = ref(false)

// UI state
const showGridSelector = ref(true)

// Window size for responsive layout
const windowSize = ref({ width: window.innerWidth, height: window.innerHeight })

// Score tracking
const { 
  saveGameScore, 
  isNewPersonalBest, 
  getGridSizeStats 
} = useScoreTracking()

// ============================================================================
// GRID SIZE SELECTOR DATA
// ============================================================================

interface GridSizeOption {
  size: number
  label: string
  difficulty: string
  description: string
}

const gridSizeOptions: GridSizeOption[] = [
  { size: 4, label: '4×4', difficulty: 'Easy', description: 'Perfect for beginners' },
  { size: 5, label: '5×5', difficulty: 'Easy', description: 'Gentle challenge' },
  { size: 6, label: '6×6', difficulty: 'Medium', description: 'Moderate difficulty' },
  { size: 7, label: '7×7', difficulty: 'Medium', description: 'Good challenge' },
  { size: 8, label: '8×8', difficulty: 'Hard', description: 'Serious challenge' },
  { size: 9, label: '9×9', difficulty: 'Hard', description: 'Expert level' },
  { size: 10, label: '10×10', difficulty: 'Expert', description: 'Ultimate challenge' }
]

// ============================================================================
// COMPUTED PROPERTIES
// ============================================================================

const gridContainerStyle = computed(() => {
  const layout = gridLayout.value
  return {
    width: `${layout.gridWidth}px`,
    height: `${layout.gridHeight}px`,
    position: 'relative' as const
  }
})

const gridLayout = computed(() => {
  const gridSize = gameState.value.gridSize
  const { width, height } = windowSize.value
  
  const slots = getSlots(width, height, gridSize)
  const slotSize = slots[0]?.width || LAYOUT_CONSTANTS.SLOT_MIN_SIZE
  
  return {
    slots,
    slotSize,
    gridWidth: slotSize * gridSize,
    gridHeight: slotSize * gridSize,
    isMobile: width <= 640
  }
})

const isLastButtonActive = computed(() => {
  return lastPairUnmatched() && !gameState.value.gameComplete
})

const errorsText = computed((): string => {
  return gameState.value.mismatchCount === 0 ? 'no errors' : 
         gameState.value.mismatchCount === 1 ? '1 error' : 
         `${gameState.value.mismatchCount} errors`
})

// Score tracking computed properties
const gridStats = computed(() => getGridSizeStats(gameState.value.gridSize))
const isNewBest = computed(() => gameState.value.gameComplete && isNewPersonalBest(gameState.value.gridSize, gameState.value.mismatchCount))

const bestText = computed(() => {
  const best = gridStats.value.best
  if (best === null) return 'no best yet'
  return best === 0 ? 'no errors' : 
         best === 1 ? '1 error' : 
         `${best} errors`
})

const averageText = computed(() => {
  const average = gridStats.value.average
  if (average === null) return 'no average yet'
  return average === 0 ? 'no errors' : 
         average === 1 ? '1 error' : 
         `${average.toFixed(1)} errors`
})

// ============================================================================
// PLACEHOLDER FUNCTIONS (to be implemented in later phases)
// ============================================================================

/**
 * Generate Latin square number distribution for fair puzzles
 * Uses constraint-based generation to ensure each number appears exactly once per row/column
 */
function generateLatinSquare(gridSize: number): number[][] {
  // Start with a shuffled first row to add randomness
  const firstRow = Array.from({ length: gridSize }, (_, i) => i)
  shuffleArray(firstRow)
  const rows: number[][] = [firstRow]

  // Generate remaining rows using constraint satisfaction
  while (rows.length < gridSize) {
    let newRow: number[] | null = null
    
    // Keep trying until we find a valid row
    while (newRow === null) {
      // Calculate available candidates for each column position
      const candidatesByColumn = Array.from({ length: gridSize }, 
        () => Array.from({ length: gridSize }, (_, i) => i)
      )
      
      // Remove values already used in each column
      for (const existingRow of rows) {
        for (let col = 0; col < gridSize; col++) {
          const usedValue = existingRow[col]
          const index = candidatesByColumn[col].indexOf(usedValue)
          if (index !== -1) {
            candidatesByColumn[col].splice(index, 1)
          }
        }
      }
      
      newRow = selectForRow(candidatesByColumn, gridSize)
      if (newRow === null) {
        console.log('MemoryKu: Retrying Latin square generation')
      }
    }
    
    rows.push(newRow)
  }
  
  // Convert numbering for display: 10x10 uses 0-9, others use 1-N
  if (gridSize === 10) {
    // Keep 0-based for 10x10 (digits 0-9)
    return rows
  } else {
    // Convert to 1-based for other sizes (1-N)
    return rows.map(row => row.map(val => val + 1))
  }
}

/**
 * Select valid values for a single row given column constraints
 */
function selectForRow(candidatesByColumn: number[][], gridSize: number): number[] | null {
  let attempts = 0
  
  // Try up to 10 times to find a valid arrangement
  while (attempts < 10) {
    attempts++
    
    const availableValues = new Set<number>(Array.from({ length: gridSize }, (_, i) => i))
    const row: number[] = []
    let validArrangement = true

    // For each column position, pick from available candidates
    for (const candidates of candidatesByColumn) {
      const validChoices = Array.from(availableValues).filter(value => candidates.includes(value))
      shuffleArray(validChoices)

      if (validChoices.length === 0) {
        validArrangement = false
        break
      }

      const chosenValue = validChoices[0]
      row.push(chosenValue)
      availableValues.delete(chosenValue)
    }

    if (validArrangement) {
      return row
    }
  }
  
  return null // Failed to find valid arrangement
}

/**
 * Fisher-Yates shuffle algorithm for randomizing arrays
 */
function shuffleArray<T>(array: T[]): void {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[array[i], array[j]] = [array[j], array[i]]
  }
}

/**
 * Generate responsive grid layout for MemoryKu
 * Exact port of Angular MemoryKuSetupService.getSlots()
 */
function getSlots(windowWidth: number, windowHeight: number, gridSize: number = 4): SlotConfig[] {
  // Calculate navbar height (responsive: min mobile, max desktop)
  const navBarHeight = Math.max(
    LAYOUT_CONSTANTS.NAVBAR_HEIGHT_MIN, 
    Math.min(LAYOUT_CONSTANTS.NAVBAR_HEIGHT_MAX, windowWidth * LAYOUT_CONSTANTS.NAVBAR_HEIGHT_SCALE)
  )
  const availableHeight = windowHeight - navBarHeight - LAYOUT_CONSTANTS.STATUS_MESSAGE_SPACE
  
  // Calculate margins for container (used by CSS)
  const isMobile = windowWidth <= 640
  const leftMargin = isMobile ? 
    LAYOUT_CONSTANTS.LEFT_MARGIN_MOBILE : 
    LAYOUT_CONSTANTS.LEFT_MARGIN_DESKTOP
  
  const rightMargin = isMobile ? 
    LAYOUT_CONSTANTS.LEFT_MARGIN_MOBILE : 
    20 // Smaller right margin on desktop for aesthetic left alignment
  
  // Calculate available width for grid with margins
  const availableWidthWithMargins = windowWidth - leftMargin - rightMargin
  const gridSlotSizeWithMargins = Math.floor(availableWidthWithMargins / gridSize)
  
  // Calculate available width for full-screen grid (no margins)
  const availableWidthFullScreen = windowWidth
  const gridSlotSizeFullScreen = Math.floor(availableWidthFullScreen / gridSize)
  
  // Determine final grid positioning - grid positioned at container origin
  let finalGridSlotSize: number
  let useFullScreenWidth = false
  const gridX = 0  // Start at 0 - no centering, container handles positioning
  
  // On mobile/small screens, always use full screen width for better tile size
  if (isMobile || gridSlotSizeWithMargins < LAYOUT_CONSTANTS.SLOT_MIN_SIZE) {
    // Use full-screen width when mobile or when margins would make tiles too small
    finalGridSlotSize = Math.min(gridSlotSizeFullScreen, LAYOUT_CONSTANTS.SLOT_MAX_SIZE)
    useFullScreenWidth = true
  } else if (gridSlotSizeWithMargins > LAYOUT_CONSTANTS.SLOT_MAX_SIZE) {
    // Tiles would be too large, use max size
    finalGridSlotSize = LAYOUT_CONSTANTS.SLOT_MAX_SIZE
  } else {
    // Use calculated size with margins
    finalGridSlotSize = Math.max(LAYOUT_CONSTANTS.SLOT_MIN_SIZE, gridSlotSizeWithMargins)
  }
  
  const gridY = 0  // Start at 0 since container handles top spacing
  
  const slots: SlotConfig[] = []
  
  // Generate slot configs with proper positioning
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      slots.push({
        x: gridX + (col * finalGridSlotSize),
        y: gridY + (row * finalGridSlotSize),
        width: finalGridSlotSize,
        height: finalGridSlotSize,
        backgroundColor: MEMORYKU_THEME.SLOT_BACKGROUND,
        borderColor: MEMORYKU_THEME.SLOT_BORDER,
        borderWidth: LAYOUT_CONSTANTS.BORDER_WIDTH,
        borderRadius: LAYOUT_CONSTANTS.BORDER_RADIUS,
        clickable: true,
        visible: true
      })
    }
  }
  
  return slots
}

/**
 * Calculate appropriate font size based on slot dimensions
 */
function calculateFontSize(slotSize: number): number {
  const FONT_SCALE = 0.75
  const FONT_MIN = 18
  const FONT_MAX = 60
  
  const fontSize = Math.max(FONT_MIN, Math.min(FONT_MAX, slotSize * FONT_SCALE))
  return Math.round(fontSize)
}

/**
 * Complete tile creation with Latin square values and responsive font sizing
 */
function createTiles(slots: SlotConfig[], gridSize: number): Tile[] {
  // Generate Latin square values
  const gridValues = generateLatinSquare(gridSize)
  
  // Create tiles with Latin square values
  const tiles = slots.map((slot, index) => {
    const row = Math.floor(index / gridSize)
    const col = index % gridSize
    const value = gridValues[row][col]
    const color = MEMORYKU_THEME.COLOR_PALETTE[value % MEMORYKU_THEME.COLOR_PALETTE.length]
    
    const fontSize = calculateFontSize(slot.width)
    
    const tile: Tile = {
      id: `tile-${index}`,
      slotId: `slot-${row}-${col}`,
      content: {
        type: 'number',
        value: value
      },
      config: {
        backgroundColor: color,
        textColor: MEMORYKU_THEME.TILE_TEXT,
        fontSize: fontSize,
        borderRadius: 0,
        animationDuration: MEMORYKU_THEME.TILE_FLIP_DURATION,
        animationEasing: 'ease-in-out'
      },
      faceUp: false,
      visible: true,
      animating: false,
      position: {
        x: slot.x,
        y: slot.y,
        width: slot.width,
        height: slot.height
      }
    }
    
    return tile
  })

  console.log(`MemoryKu: Created ${tiles.length} tiles for ${gridSize}×${gridSize} grid`)
  return tiles
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Utility function for delays
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// ============================================================================
// GAME FUNCTIONS
// ============================================================================

function onGridSizeSelected(size: number): void {
  console.log(`MemoryKu: Selected grid size ${size}×${size}`)
  startNewGame(size)
  showGridSelector.value = false
}

function startNewGame(gridSize: number): void {
  console.log(`MemoryKu: Starting new ${gridSize}×${gridSize} game`)
  
  // Update grid size
  gameState.value.gridSize = gridSize
  
  // Generate slots and tiles
  const { width, height } = windowSize.value
  const slots = getSlots(width, height, gridSize)
  const tiles = createTiles(slots, gridSize)
  
  // Reset game state
  gameState.value = {
    gridSize,
    tiles,
    slots,
    flippedTiles: [],
    isProcessing: false,
    mismatchCount: 0,
    gameComplete: false,
    gameInProgress: false,
    lastPairFlipped: [],
    lastMismatchTiles: [],
    showingLastMismatch: false
  }
  
  console.log(`MemoryKu: Created ${tiles.length} tiles for ${gridSize}×${gridSize} grid`)
}

/**
 * Handle tile click with game logic and queuing
 */
async function onTileClick(tileId: string): Promise<void> {
  // If game is complete, ignore clicks
  if (gameState.value.gameComplete) {
    return
  }

  // If currently processing, queue the click
  if (gameState.value.isProcessing) {
    clickQueue.value.push(tileId)
    console.log(`MemoryKu: Queued click for tile ${tileId}`)
    return
  }

  // Process the click immediately
  await processTileClick(tileId)
  
  // Process queued clicks
  await processClickQueue()
}

/**
 * Process a single tile click
 */
async function processTileClick(tileId: string): Promise<void> {
  const tile = gameState.value.tiles.find(t => t.id === tileId)
  if (!tile || tile.faceUp) {
    return
  }

  console.log(`MemoryKu: Flipping tile ${tileId} (value: ${tile.content.value})`)

  // Mark game as started on first click
  const gameInProgress = gameState.value.gameInProgress || gameState.value.flippedTiles.length > 0
  
  // Flip the tile (Vue reactivity will update UI)
  tile.faceUp = true
  tile.config.hasGoldBorder = true
  gameState.value.flippedTiles.push(tileId)
  gameState.value.gameInProgress = gameInProgress

  // Check for match when two tiles are flipped
  if (gameState.value.flippedTiles.length === 2) {
    await processMatch(gameState.value.flippedTiles)
  }
}

/**
 * Process queued clicks one by one
 */
async function processClickQueue(): Promise<void> {
  if (processingQueue.value || clickQueue.value.length === 0) {
    return
  }

  processingQueue.value = true

  while (clickQueue.value.length > 0) {
    // Wait for any current processing to complete
    while (gameState.value.isProcessing) {
      await delay(50)
    }

    const nextTileId = clickQueue.value.shift()
    if (nextTileId) {
      console.log(`MemoryKu: Processing queued click for tile ${nextTileId}`)
      await processTileClick(nextTileId)
    }
  }

  processingQueue.value = false
}

/**
 * Process match logic when two tiles are flipped
 */
async function processMatch(flippedTileIds: string[]): Promise<void> {
  // Save the current pair for the Last button and set processing state
  gameState.value.lastPairFlipped = [...flippedTileIds]
  gameState.value.isProcessing = true

  // Wait for flip animation to complete
  await delay(MEMORYKU_THEME.MATCH_PROCESSING_DELAY)

  const [firstTileId, secondTileId] = flippedTileIds
  const firstTile = gameState.value.tiles.find(t => t.id === firstTileId)
  const secondTile = gameState.value.tiles.find(t => t.id === secondTileId)

  if (firstTile && secondTile) {
    const isMatch = firstTile.content.value === secondTile.content.value

    if (isMatch) {
      console.log(`MemoryKu: Match found! Value: ${firstTile.content.value}`)
      await handleMatch(firstTileId, secondTileId)
    } else {
      console.log(`MemoryKu: No match. Values: ${firstTile.content.value}, ${secondTile.content.value}`)
      await handleMismatch(firstTileId, secondTileId)
    }
  }

  // Reset flipped tiles and processing state
  gameState.value.flippedTiles = []
  gameState.value.isProcessing = false

  // Check for game completion
  checkGameComplete()
}

/**
 * Handle successful match
 */
async function handleMatch(firstTileId: string, secondTileId: string): Promise<void> {
  // Clear any previous mismatch tiles since this play was successful
  gameState.value.tiles.forEach(t => {
    if (t.id === firstTileId || t.id === secondTileId) {
      // Keep matched tiles face up and remove gold border
      t.config.hasGoldBorder = false
    }
  })

  gameState.value.lastMismatchTiles = []

  // Animate match success
  await delay(200)
}

/**
 * Handle failed match (mismatch)
 */
async function handleMismatch(firstTileId: string, secondTileId: string): Promise<void> {
  // Increment error counter and track failed tiles
  gameState.value.mismatchCount++
  
  // Wait before flipping back
  await delay(MEMORYKU_THEME.MISMATCH_DISPLAY_DURATION)

  // Flip tiles back face down
  gameState.value.tiles.forEach(t => {
    if (t.id === firstTileId || t.id === secondTileId) {
      t.faceUp = false
      t.config.hasGoldBorder = false
    }
  })

  gameState.value.lastMismatchTiles = [firstTileId, secondTileId]
}

/**
 * Check if game is complete
 */
function checkGameComplete(): void {
  const allRevealed = gameState.value.tiles.every(tile => tile.faceUp)
  
  if (allRevealed) {
    console.log(`MemoryKu: Puzzle complete! Errors: ${gameState.value.mismatchCount}`)
    
    // Create game score object
    const gameScore: GameScore = {
      gridSize: gameState.value.gridSize,
      errors: gameState.value.mismatchCount,
      completedAt: new Date()
    }
    
    // Save the score
    saveGameScore(gameScore)
    console.log('Game score created:', gameScore)
    
    gameState.value.gameComplete = true

    // Trigger celebration effect (will implement in Phase 6.2)
    celebrateCompletion()
  }
}

/**
 * Trigger celebration effects on completion
 */
async function celebrateCompletion() {
  // Placeholder - will implement confetti in Phase 6.2
  console.log('MemoryKu: Celebration effects triggered!')
}

function showLastPair(): void {
  // Placeholder - will implement in Phase 5.2
  console.log('Show last pair requested')
}

function lastPairUnmatched(): boolean {
  // Placeholder - will implement in Phase 5.2
  return false
}

function getDifficultyClass(difficulty: string): string {
  return difficulty.toLowerCase().replace(' ', '-')
}

function getDifficultyLabel(gridSize: number): string {
  if (gridSize <= 4) return 'Easy'
  if (gridSize <= 6) return 'Medium'
  if (gridSize <= 8) return 'Hard'
  return 'Expert'
}

function getBestScoreText(gridSize: number): string {
  // Placeholder - will implement in Phase 5.1
  return 'No best score yet'
}

function getTileStyle(tile: Tile) {
  return {
    left: `${tile.position?.x || 0}px`,
    top: `${tile.position?.y || 0}px`,
    width: `${tile.position?.width || 50}px`,
    height: `${tile.position?.height || 50}px`,
    fontSize: `${tile.config.fontSize}px`,
    border: tile.config.hasGoldBorder ? `3px solid ${MEMORYKU_THEME.SELECTED_BORDER}` : 'none'
  }
}

function getTileFaceStyle(tile: Tile) {
  return {
    backgroundColor: tile.config.backgroundColor,
    color: tile.config.textColor
  }
}

// ============================================================================
// LIFECYCLE AND EVENT HANDLERS
// ============================================================================

onMounted(() => {
  const handleResize = () => {
    windowSize.value = { width: window.innerWidth, height: window.innerHeight }
    // Note: Removed startNewGame() call that was resetting game state on iOS resize events
    // Game state now persists across device orientation/sleep cycles like the Angular version
  }
  
  window.addEventListener('resize', handleResize)
  onUnmounted(() => window.removeEventListener('resize', handleResize))
})
</script>

<style scoped>
/* ============================================================================
   MAIN APP STYLES
   ============================================================================ */

#app {
  width: 100vw;
  height: 100vh;
  background-color: #f3f4f6;
  overflow: hidden;
}

/* ============================================================================
   GRID SIZE SELECTOR STYLES
   ============================================================================ */

.grid-size-selector {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  padding: 20px;
  text-align: center;
}

.grid-size-selector h2 {
  color: #1f2937;
  margin-bottom: 20px;
}

.grid-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  max-width: 600px;
  margin: 0 auto;
}

.grid-option {
  border: 2px solid #10b981;
  border-radius: 8px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
}

.grid-option:hover {
  border-color: #ffd700;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.size-label {
  font-size: 1.25rem;
  font-weight: bold;
}

/* ============================================================================
   GAME CONTAINER STYLES
   ============================================================================ */

.game-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  min-height: 60px;
}

.back-btn, .new-game-btn, .last-btn, .select-size-btn {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.back-btn:hover, .new-game-btn:hover, .last-btn:hover, .select-size-btn:hover {
  background: #2563eb;
}

.last-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.game-info {
  display: flex;
  gap: 20px;
  align-items: center;
}

.grid-size-display {
  font-weight: bold;
  font-size: 1.1rem;
}

.error-count {
  color: #6b7280;
}

.game-controls {
  display: flex;
  gap: 10px;
}

/* ============================================================================
   GAME GRID STYLES
   ============================================================================ */

.game-grid-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.game-grid {
  position: relative;
}

/* ============================================================================
   TILE STYLES
   ============================================================================ */

.memory-tile {
  position: absolute;
  cursor: pointer;
  perspective: 1000px;
  user-select: none;
  background-color: #6b7280;
}

.tile-content {
  width: 100%;
  height: 100%;
  transition: transform 600ms ease-in-out;
  transform-style: preserve-3d;
  position: relative;
}

.memory-tile.face-up .tile-content {
  transform: rotateY(180deg);
}

.memory-tile.face-down .tile-content {
  transform: rotateY(0deg);
}

.tile-face, .tile-back {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  border: 1px solid #000000;
  box-sizing: border-box;
}

.tile-face {
  transform: rotateY(180deg);
}

.tile-back {
  background-color: #6b7280;
  transform: rotateY(0deg);
}

/* ============================================================================
   COMPLETION OVERLAY STYLES
   ============================================================================ */

.completion-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.completion-stats {
  text-align: center;
  padding: 30px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
  max-width: 400px;
  margin: 0 auto;
}

.completion-stats h2 {
  margin: 0 0 20px 0;
  color: #10b981;
  font-size: 1.5rem;
}

.grid-info {
  margin-bottom: 20px;
}

.grid-size {
  font-size: 1.25rem;
  font-weight: bold;
  color: #1f2937;
}

.completion-stats .difficulty {
  color: #6b7280;
  margin-left: 8px;
}

.current-score {
  background: #f3f4f6;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.current-score .label {
  display: block;
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 5px;
}

.current-score .value {
  font-size: 1.1rem;
  font-weight: bold;
  color: #1f2937;
}

.current-score .new-best {
  display: block;
  color: #10b981;
  font-weight: bold;
  font-size: 0.9rem;
  margin-top: 5px;
}

.stats-summary {
  background: #f9fafb;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.stats-line {
  font-size: 0.95rem;
  color: #374151;
  margin-bottom: 5px;
}

.games-played {
  font-size: 0.85rem;
  color: #6b7280;
}

.actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 20px;
}

/* ============================================================================
   RESPONSIVE STYLES
   ============================================================================ */

@media (max-width: 640px) {
  .navbar {
    padding: 10px;
    flex-wrap: wrap;
    min-height: auto;
  }
  
  .game-info {
    gap: 10px;
    font-size: 0.9rem;
  }
  
  .game-controls {
    gap: 5px;
  }
  
  .back-btn, .new-game-btn, .last-btn {
    padding: 6px 12px;
    font-size: 0.9rem;
  }
  
  .grid-options {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
  
  .game-grid-container {
    padding: 0;
  }
  
  .grid-option {
    padding: 12px;
  }
  
  .completion-stats {
    margin: 20px;
    padding: 20px;
  }
  
  .actions {
    flex-direction: column;
  }
}
</style>